import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

import { EmailSetupBlocks } from "@/components/agent/EmailSetupBlocks";
import type { AssistantIdentity, Contact } from "@/types/contacts";

/**
 * `EmailSetupDialog` — the §3.9 e-mail setup, in the §3.7 grammar (S2):
 * benefit headline + proofs (the e-mail deck) over the three blocks
 * (`EmailSetupBlocks`). Opened by the hero's e-mail pencil. E-mail has NO
 * disconnect and no journey stepper — the mailbox is provisioned, not
 * connected; ② is the one live write, ①/③ show their honest gated state
 * until the mailsystem backend halves land.
 */
export function EmailSetupDialog({
  assistant,
  owner,
  onClose,
  onChanged,
}: {
  assistant: AssistantIdentity | null;
  owner: Contact | null;
  onClose: () => void;
  onChanged?: () => void;
}) {
  const { t } = useTranslation();

  // Esc closes THIS dialog only (capture + stopImmediatePropagation — the
  // same pane-protecting pattern as every hero dialog).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopImmediatePropagation();
      onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="email-setup-dialog"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white shadow-xl dark:bg-coffee-900">
        {/* §3.7 header — the e-mail deck (indigo accent, the tab's hue). */}
        <div className="space-y-2 border-b border-indigo-400/50 bg-indigo-50/80 px-6 py-5 dark:border-indigo-400/30 dark:bg-indigo-950/40">
          <div className="flex items-start gap-2">
            <h3 className="min-w-0 flex-1 font-serif-display text-xl leading-snug text-coffee-900 dark:text-cream-100">
              {t("channels.benefit.email.headline")}
            </h3>
            <button
              type="button"
              onClick={onClose}
              aria-label={t("common.close")}
              title={t("common.close")}
              data-testid="email-setup-dialog-close"
              className="shrink-0 rounded-lg p-1.5 text-coffee-500 transition-colors hover:bg-white/60 hover:text-coffee-800 dark:text-cream-400 dark:hover:bg-coffee-800 dark:hover:text-cream-100"
            >
              <X size={16} />
            </button>
          </div>
          <ul className="space-y-1">
            {([1, 2, 3] as const).map((n) => {
              const text = t(`channels.benefit.email.proof${n}`, { defaultValue: "" });
              if (!text) return null;
              return (
                <li
                  key={n}
                  className="flex items-start gap-2 text-body-sm text-coffee-700 dark:text-cream-300"
                >
                  <span aria-hidden className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-gold-500" />
                  <span>{text}</span>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="space-y-3 px-6 py-4">
          <EmailSetupBlocks assistant={assistant} owner={owner} onChanged={onChanged} />
          <div className="flex justify-end border-t border-cream-200/70 pt-3 dark:border-coffee-800/60">
            <button
              type="button"
              onClick={onClose}
              data-testid="email-setup-done"
              className="rounded-md bg-gold-500 px-4 py-1.5 text-body-sm font-semibold text-coffee-900 transition-colors hover:bg-gold-400"
            >
              {t("agent.emailSetup.doneButton")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
